package com.evoapps.evoshift.domain.capacity

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.Availability
import com.evoapps.evoshift.domain.model.AvailabilityRequest
import com.evoapps.evoshift.domain.model.AvailabilitySlot
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.CompensationModel
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.schedule.HoursTarget
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import com.evoapps.evoshift.domain.schedule.StaffProfiles
import com.evoapps.evoshift.domain.schedule.TargetSource
import com.evoapps.evoshift.domain.util.calculateDurationHours
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus

/*
 * Capacity before planning (SH-48): how many hours the month (or part of it) needs, how many the
 * team offered, what they are contracted for, and who has not answered yet. It is the app's version
 * of the summary the owner's spreadsheet showed after importing availability ("ŁĄCZNA
 * DYSPOZYCYJNOŚĆ (bez UoP/Właściciela)": available days × 7.5 h, the owner and employment
 * contracts left out).
 */

/** Days of [year]-[month]: the first to the last. */
fun monthRange(year: Int, month: Int): ScheduleRange {
    val first = LocalDate(year, month, 1)
    return ScheduleRange(first, first.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1)))
}

/** The days of [range] that fall in [month] (null when none do): planning tools work on part of a month. */
fun ScheduleRange.within(month: ScheduleRange): ScheduleRange? {
    val start = maxOf(start, month.start)
    val end = minOf(endInclusive, month.endInclusive)
    return if (start <= end) ScheduleRange(start, end) else null
}

/** Every day of the range, in order. */
fun ScheduleRange.days(): List<LocalDate> =
    generateSequence(start) { it.plus(DatePeriod(days = 1)) }.takeWhile { it <= endInclusive }.toList()

/** The number of days in the range. */
val ScheduleRange.dayCount: Int get() = days().size

/** How far a person answered the availability request for the month. */
enum class AvailabilityAnswer {
    /** Sent the form (Availability.isFilled). */
    SUBMITTED,
    /** Started it (days saved) but did not send it. */
    STARTED,
    /** Nothing for the month. */
    NONE
}

/** Why a person's available hours are not in the team's total (as in their spreadsheet's summary). */
enum class CapacityExclusion {
    /** The workplace's owner: plans their own hours. */
    OWNER,
    /** An employment contract (umowa o pracę): their hours follow the contract, not availability. */
    EMPLOYMENT_CONTRACT
}

/** One person's side of the capacity. */
data class PersonCapacity(
    val userId: String,
    val displayName: String,
    val role: String?,
    /** A person without the app (added by a manager): they cannot answer or get a reminder. */
    val isShadow: Boolean,
    val answer: AvailabilityAnswer,
    /** Days in the range they can work (not marked unavailable, not on approved leave). */
    val availableDays: Int,
    /** Hours those days give (see [CapacityCalculator.dayHours]). */
    val availableHours: Double,
    /** Days in the range of approved leave (not available, whatever the form says). */
    val absentDays: Int,
    /** Hours already assigned to them in the range. */
    val plannedHours: Double,
    /** Their contract here; null when the viewer may not read pay terms. */
    val contract: SystemContractType?,
    /** Their hours target for the range; null when the viewer may not read pay terms. */
    val target: HoursTarget?,
    /** The same target for the whole month (what the contract says). */
    val monthlyTarget: HoursTarget?,
    /** Set when their hours are left out of the team's available total. */
    val excludedAs: CapacityExclusion?,
    /** SH-173: the month was given through the e-mailed link's web form, not in the app. */
    val answeredByEmail: Boolean = false
) {
    val isCounted: Boolean get() = excludedAs == null
    /** Has not sent the form: listed as missing. */
    val isMissing: Boolean get() = answer != AvailabilityAnswer.SUBMITTED
    /** A reminder reaches them (missing, and they have the app). */
    val canBeReminded: Boolean get() = isMissing && !isShadow
}

/** Demand against availability and contracts for a range of a roster's month. */
data class CapacitySummary(
    val rosterId: String,
    val workplaceId: String,
    val year: Int,
    val month: Int,
    /** The days counted (inside the month). */
    val range: ScheduleRange,
    /** Hours of all shifts in the range (open and assigned): the demand from the skeleton. */
    val demandHours: Double,
    val demandShifts: Int,
    /** Of those, the shifts nobody has yet. */
    val openHours: Double,
    val openShifts: Int,
    /** Available hours of the counted people (without the owner and employment contracts). */
    val availableHours: Double,
    /** Available hours of the people left out (owner, employment contracts), shown on their own. */
    val excludedAvailableHours: Double,
    /** The full-time working-time norm of the range (Polish holidays included). */
    val nominalHours: Int,
    /** The same norm for the whole month. */
    val monthNominalHours: Int,
    /** Whether contracts and targets could be read (owner and financial managers). */
    val payTermsVisible: Boolean,
    /** Sum of the known targets of the counted people for the range; null when not visible. */
    val targetHours: Double?,
    val people: List<PersonCapacity>,
    /** The month's availability request (its period and deadline), if one was sent. */
    val request: AvailabilityRequest?
) {
    val isWholeMonth: Boolean get() = range == monthRange(year, month)
    /** Demand minus available hours: above 0 the team offered too little, below 0 there is slack. */
    val gap: Double get() = demandHours - availableHours
    /** People who have not sent their availability. */
    val missing: List<PersonCapacity> get() = people.filter { it.isMissing }
    /** Of those, the ones a reminder reaches. */
    val remindable: List<PersonCapacity> get() = missing.filter { it.canBeReminded }
}

/** What [CapacityCalculator.calculate] reads; built from the repositories by GetRosterCapacityUseCase. */
data class CapacityInput(
    val rosterId: String,
    val workplaceId: String,
    val workplaceOwnerId: String?,
    val year: Int,
    val month: Int,
    /** The days to count (null: the whole month); cut to the month. */
    val range: ScheduleRange?,
    /** The roster's shifts (the whole month). */
    val shifts: List<Shift>,
    /** Active members of the workplace. */
    val team: List<User>,
    /** Each person's availability for the month (missing: none). */
    val availability: Map<String, Availability>,
    /** Absences at the workplace (only approved ones of the person count). */
    val absences: List<Absence>,
    /**
     * Pay terms per user id: null when the viewer may not read them at all; a failed read for a
     * person leaves their target unknown.
     */
    val payTerms: Map<String, Result<FinancialEngagement?>>?,
    val slots: List<AvailabilitySlot>,
    /** Full-time norm of the month and of a range (PayrollRules.workingHours). */
    val monthNominalHours: Int,
    val nominalHours: (ScheduleRange) -> Int,
    val request: AvailabilityRequest? = null,
    val maxDayHours: Double = CapacityCalculator.DEFAULT_DAY_HOURS
)

object CapacityCalculator {
    /**
     * The most one available day counts: a full working day (Kodeks pracy art. 129), like the
     * availability form's own count. A day without a preferred slot, or with a slot longer than
     * this (e.g. "All day"), counts this much.
     */
    const val DEFAULT_DAY_HOURS = 8.0

    /** Length of a slot in hours ("24:00" ends the day; an end before the start runs past midnight). */
    fun slotHours(slot: AvailabilitySlot): Double {
        val hours = calculateDurationHours(slot.startTime, slot.endTime)
        return if (hours == 0.0 && slot.startTime.trim() == slot.endTime.trim()) 24.0 else hours
    }

    /**
     * Hours one day of availability offers:
     * - marked unavailable: 0;
     * - available in a preferred slot: the slot's length, at most [maxDayHours];
     * - available without a slot: [maxDayHours];
     * - not touched: in a sent form the day showed as available, so [maxDayHours]; in an unsent one
     *   (or no form) nothing was said, so 0.
     */
    fun dayHours(
        preference: DayPreference?,
        formSent: Boolean,
        slots: Map<String, AvailabilitySlot>,
        maxDayHours: Double = DEFAULT_DAY_HOURS
    ): Double = when {
        preference == null -> if (formSent) maxDayHours else 0.0
        preference.status == AvailabilityStatus.UNAVAILABLE -> 0.0
        else -> preference.preferredSlotId?.let { slots[it] }?.let { minOf(slotHours(it), maxDayHours) } ?: maxDayHours
    }

    /**
     * A monthly target for part of the month: an employment contract's FTE × the range's norm (the
     * law's own measure); declared hours in proportion to the days of the month in the range.
     */
    fun rangeTarget(monthly: HoursTarget, pay: FinancialEngagement?, rangeNominal: Int, rangeDays: Int, monthDays: Int): HoursTarget {
        val hours = monthly.hours ?: return monthly
        if (rangeDays >= monthDays) return monthly
        val salary = pay?.compensation as? CompensationModel.MonthlyFixed
        return when (monthly.source) {
            TargetSource.FULL_TIME_SHARE -> HoursTarget.of((salary?.fte ?: 1.0) * rangeNominal, monthly.source)
            else -> HoursTarget.of(hours * rangeDays / monthDays, monthly.source)
        }
    }

    fun calculate(input: CapacityInput): CapacitySummary {
        val month = monthRange(input.year, input.month)
        val range = input.range?.within(month) ?: month
        val days = range.days()
        val isoDays = days.map { it.toString() }.toSet()
        val slots = input.slots.associateBy { it.id }
        val monthDays = month.dayCount
        val rangeNominal = input.nominalHours(range)

        val inRange = input.shifts.filter { it.date in isoDays }
        fun Shift.hours() = calculateDurationHours(plannedTime.start, plannedTime.end)
        val open = inRange.filter { it.userId == null }

        val people = input.team.map { user ->
            val engagement = user.engagements[input.workplaceId]
            val form = input.availability[user.uid]
            val answer = when {
                form == null -> AvailabilityAnswer.NONE
                form.isFilled -> AvailabilityAnswer.SUBMITTED
                form.days.isNotEmpty() -> AvailabilityAnswer.STARTED
                else -> AvailabilityAnswer.NONE
            }
            val absent = StaffProfiles.absentDates(user.uid, input.absences, input.year, input.month).filter { it in isoDays }.toSet()
            var availableDays = 0
            var availableHours = 0.0
            days.forEach { date ->
                if (date.toString() in absent) return@forEach
                val hours = dayHours(form?.days?.get(date.day), answer == AvailabilityAnswer.SUBMITTED, slots, input.maxDayHours)
                if (hours > 0) {
                    availableDays++
                    availableHours += hours
                }
            }

            val payRead = input.payTerms?.get(user.uid)
            val pay = payRead?.getOrNull()
            val monthly = input.payTerms?.let {
                StaffProfiles.monthlyTarget(pay, payTermsReadable = payRead?.isSuccess ?: false, nominalHours = input.monthNominalHours)
            }
            val contract = input.payTerms?.let { pay?.contract }
            val isOwner = engagement?.permission == UserPermission.OWNER || user.uid == input.workplaceOwnerId
            PersonCapacity(
                userId = user.uid,
                displayName = user.displayName,
                role = engagement?.role,
                isShadow = user.isShadow,
                answer = answer,
                availableDays = availableDays,
                availableHours = availableHours,
                absentDays = absent.size,
                plannedHours = inRange.filter { it.userId == user.uid }.sumOf { it.hours() },
                contract = contract,
                target = monthly?.let { rangeTarget(it, pay, rangeNominal, days.size, monthDays) },
                monthlyTarget = monthly,
                excludedAs = when {
                    // SH-133: a partner without a contract counts like the owner.
                    isOwner || contract == SystemContractType.OWNER_PARTNER -> CapacityExclusion.OWNER
                    contract == SystemContractType.STANDARD_EMPLOYMENT -> CapacityExclusion.EMPLOYMENT_CONTRACT
                    else -> null
                },
                answeredByEmail = form?.viaEmailLink == true
            )
        }.sortedWith(compareBy<PersonCapacity> { it.excludedAs != null }.thenBy { it.displayName.lowercase() })

        val counted = people.filter { it.isCounted }
        return CapacitySummary(
            rosterId = input.rosterId,
            workplaceId = input.workplaceId,
            year = input.year,
            month = input.month,
            range = range,
            demandHours = inRange.sumOf { it.hours() },
            demandShifts = inRange.size,
            openHours = open.sumOf { it.hours() },
            openShifts = open.size,
            availableHours = counted.sumOf { it.availableHours },
            excludedAvailableHours = people.filterNot { it.isCounted }.sumOf { it.availableHours },
            nominalHours = rangeNominal,
            monthNominalHours = input.monthNominalHours,
            payTermsVisible = input.payTerms != null,
            targetHours = input.payTerms?.let { counted.mapNotNull { p -> p.target?.hours }.sum() },
            people = people,
            request = input.request
        )
    }
}
